'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/brand-kit/icon-sprite'

import { cn } from '@/lib/utils'
import { keepKeyboardFocusInCarousel } from '@/lib/keyboard-focus-in-view'
import { Button } from '@/components/homepage-october/ui/button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  // FRIDGE-1582: Embla's own focus handling (watchFocus) moves the carousel only
  // when focus follows the Tab key within 10 ms, so on a busy page it did not
  // (production 10/17/2026, /collections/all at 375: 32 to 47 ms in a busy run,
  // three cards in a row left 4 to 34 px in view). It is off, and
  // keepKeyboardFocusInCarousel below decides for keyboard focus alone, the same
  // way on every page.
  const [carouselRef, api] = useEmblaCarousel(
    {
      watchFocus: false,
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  )


  // The embla api IS an external store, so it is read through
  // useSyncExternalStore rather than mirrored into state by an effect
  // (react-hooks/set-state-in-effect). Three things improve:
  //
  //  - no render-then-correct: the first render already has the real values
  //    instead of false, false;
  //  - the LEAK is gone. The old cleanup removed only the 'select' listener and
  //    never the 'reInit' one, so every api change left a listener behind;
  //  - subscribe/unsubscribe are symmetric by construction.
  const subscribeToApi = React.useCallback(
    (onStoreChange: () => void) => {
      if (!api) return () => {}
      api.on('select', onStoreChange)
      api.on('reInit', onStoreChange)
      return () => {
        api.off('select', onStoreChange)
        api.off('reInit', onStoreChange)
      }
    },
    [api],
  )
  const canScrollPrev = React.useSyncExternalStore(
    subscribeToApi,
    () => (api ? api.canScrollPrev() : false),
    () => false,
  )
  const canScrollNext = React.useSyncExternalStore(
    subscribeToApi,
    () => (api ? api.canScrollNext() : false),
    () => false,
  )

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext],
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  // FRIDGE-1582 (FRIDGE-1508 K12): a card or control that takes keyboard focus is
  // brought fully inside the carousel (lib/keyboard-focus-in-view.ts). A tap or
  // click moves nothing.
  React.useEffect(() => {
    if (!api) return
    return keepKeyboardFocusInCarousel(api, orientation === 'horizontal' ? 'x' : 'y')
  }, [api, orientation])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={compactClassName(cn('relative', className))}
        role="region"
        aria-roledescription="carousel"

        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className={compactClassName("overflow-hidden")}
      data-slot="carousel-content"
    >
      {/* FRIDGE-1582: Embla moves this track with an inline transform, in script, so
          it never transitions in CSS (transition-none). The global reduced-motion
          rule (app/globals.css) gives every element a 0.01 ms transition, and on
          the track that left getBoundingClientRect one frame behind Embla's jump:
          revealInCarousel (lib/keyboard-focus-in-view.ts) moved the same distance
          twice, and with reduced motion on 10 to 15 of 18 focused cards ended
          outside the carousel (production 10/18/2026, a blog tag page at 375). */}
      <div
        className={compactClassName(cn(
          'flex transition-none',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        ))}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"

      className={compactClassName(cn(
        'min-w-0 shrink-0 grow-0',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      ))}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  const stopAndScrollPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    scrollPrev()
    // If consumer passed an onClick, call it too (after preventing navigation)
    // @ts-ignore - allow passing through any optional onClick
    props.onClick?.(e)
  }

  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Button

      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -left-12 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollPrev}
      onMouseDown={stop}
      onTouchStart={stop}
      onClick={stopAndScrollPrev}
      {...props}
    >
      <ChevronLeftIcon />
      <span className={compactClassName("sr-only")}>Previous slide</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  const stopAndScrollNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    scrollNext()
    // Call consumer handler if provided (after preventing navigation)
    // @ts-ignore
    props.onClick?.(e)
  }

  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Button

      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -right-12 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      disabled={!canScrollNext}
      onMouseDown={stop}
      onTouchStart={stop}
      onClick={stopAndScrollNext}
      {...props}
    >
      <ChevronRightIcon />
      <span className={compactClassName("sr-only")}>Next slide</span>
    </Button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
}
